import Survey from '../interfaces/survey';
import Question from '../interfaces/question';
import { QuestionType } from '../interfaces/questionType';
import { getConditionalQuestion } from '../domain/question';

const defaultPagination = (cur: Question, page: number): number => {
  if (cur.pageBreak) return page + 1;
  return page;
};

interface Paginate {
  survey: Survey;
}

const paginate = ({ survey }: Paginate): Map<number, Question[]> => {
  const { questions } = survey;
  let page = 1;
  return questions
    .filter((q) => !q.conditional)
    .reduce((acc: Map<number, Question[]>, cur: Question) => {
      acc.set(page, [...(acc.get(page) || []), cur]);
      page = defaultPagination(cur, page);
      return acc;
    }, new Map());
};

interface GetPage {
  paginatedSurvey: Map<number, Question[]>;
  page: number;
  defaultValue: Question[];
}

const getPage = ({ paginatedSurvey, page, defaultValue }: GetPage): Question[] => {
  if (paginatedSurvey.has(page)) return paginatedSurvey.get(page) || defaultValue;
  return defaultValue;
};

const getQuestionIds = ({ page, survey }: { page: Question[]; survey: Survey }): number[] =>
  page
    .map((question) => {
      if (
        question.type === QuestionType.MULTIPLE ||
        question.type === QuestionType.MULTIPLE_DYNAMIC
      )
        return getQuestionIds({ page: question.questions, survey });
      if (question.type === QuestionType.RADIO_BUTTON) {
        return [
          question.id,
          ...getQuestionIds({
            page: question.radioButtonOptions
              .filter((c) => !!c.conditionalQuestionId)
              .map(
                (c) =>
                  getConditionalQuestion(c.conditionalQuestionId as number, survey) as Question,
              ),
            survey,
          }),
        ];
      }
      if (question.type === QuestionType.CHOOSE_MANY) {
        return [
          question.id,
          ...getQuestionIds({
            page: question.choices
              .filter((c) => !!c.conditionalQuestionId)
              .map(
                (c) =>
                  getConditionalQuestion(c.conditionalQuestionId as number, survey) as Question,
              ),
            survey,
          }),
        ];
      }
      return question.id;
    })
    .flat();

export { paginate, getPage, getQuestionIds };
